import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { BoolIcon } from '@/components/common/BoolIcon'
import { AddButton } from '@/components/common/AddButton'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { useProductsAdmin, useDeleteProduct } from '@/features/product/hooks'
import type { Product } from '@/features/product/types'
import { CreateProductDialog } from './components/CreateProductDialog'
import { formatError } from './components/formatError'

const TABLE_ID = 'admin-products-elements'

export function ProductsAdminPage() {
  const confirm = useConfirm()
  const products = useProductsAdmin()
  const del = useDeleteProduct()
  const [creating, setCreating] = useState(false)
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(TABLE_ID)

  const columns: ColumnDef<Product>[] = useMemo(
    () => [
      {
        key: 'code',
        header: 'Kod',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj kod...' },
        accessor: (p) => (
          <Link to={`/settings/products/${p.code}`} className="font-mono text-sky-700 hover:underline">
            {p.code}
          </Link>
        ),
        className: 'whitespace-nowrap',
        alwaysVisible: true,
      },
      {
        key: 'labelPl',
        header: 'Nazwa',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj nazwę...' },
        accessor: (p) => p.labelPl,
        alwaysVisible: true,
      },
      {
        key: 'isActive',
        header: 'Aktywny',
        sortable: true,
        filter: { kind: 'bool', trueLabel: 'Aktywne', falseLabel: 'Nieaktywne' },
        accessor: (p) => <BoolIcon value={p.isActive} />,
        className: 'w-24',
      },
      {
        key: 'actions',
        header: '',
        accessor: (p) => (
          <div className="text-right whitespace-nowrap">
            <Link
              to={`/settings/products/${p.code}`}
              className="text-xs text-sky-700 hover:text-sky-900 px-1"
            >
              Edytuj
            </Link>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                const ok = await confirm({
                  title: `Usunąć produkt ${p.code}?`,
                  description: 'Operacja zablokowana jeśli produkt należy do jakiejś konfiguracji.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(p.code, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-xs text-red-600 px-1 ml-1"
            >
              Usuń
            </button>
          </div>
        ),
        className: 'w-32',
        alwaysVisible: true,
      },
    ],
    [del, confirm],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(products.data ?? [], columns, state, {
        valueGetters: {
          code: (p) => p.code,
          labelPl: (p) => p.labelPl,
          isActive: (p) => p.isActive,
        },
        searchString: (p) => `${p.code} ${p.labelPl}`.toLowerCase(),
      }),
    [products.data, columns, state],
  )

  return (
    <div className="flex flex-col w-full">
      <ListPageHeader
        title="Produkty (elementy)"
        subtitle="Elementy umowy (fotowoltaika, magazyn, wiatrak...) - każdy ma własne etapy. Konfiguracja umowy składa się z wybranych elementów."
        actions={<AddButton onClick={() => setCreating(true)}>Dodaj produkt</AddButton>}
      />

      <div className="p-4">
        <DataTable<Product>
          tableId={TABLE_ID}
          data={filteredRows}
          columns={columns}
          rowKey={(p) => p.code}
          isLoading={products.isLoading}
          isError={products.isError}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj kodu / nazwy..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState="Brak produktów."
        />
      </div>

      {creating && <CreateProductDialog onClose={() => setCreating(false)} />}
    </div>
  )
}
